import { auth, db } from '@/config/firebase'
import { getDoc, doc } from 'firebase/firestore'

// Get selected language from localStorage
const getSelectedLanguage = () => {
  const selectedCountry = localStorage.getItem('selectedCountry')
  return selectedCountry === 'co' ? 'es' : 'en' // Default to 'en' if no selection
}

// Widget configuration by language
const WIDGET_CONFIG = {
  // English widget
  en: {
    propertyId: '68389af01b2e7f190d5548ab', 
    widgetId: '1isehqemc'
  },
  // Spanish widget
  es: {
    propertyId: '68655dc5372f80190c028e15', 
    widgetId: '1iv5vciv2'
  }
}

/**
 * Initialize tawk.to widget based on selected language
 */
export const initializeTawkWidget = () => {
  const language = getSelectedLanguage()
  const config = WIDGET_CONFIG[language] || WIDGET_CONFIG.en
  
  // Initialize script if not exists
  if (!window.Tawk_API) {
    const s1 = document.createElement('script')
    const s0 = document.getElementsByTagName('script')[0]
    
    s1.async = true
    s1.src = `https://embed.tawk.to/${config.propertyId}/${config.widgetId}`
    s1.charset = 'UTF-8'
    s1.setAttribute('crossorigin', '*')
    
    s0.parentNode.insertBefore(s1, s0)
    

  }
}

/**
 * Configure tawk.to widget attributes based on the view
 * @param {string} viewName - View name ('landing', 'login', 'app')
 */
export const setTawkAttributes = async (viewName) => {
  // Initialize widget if not initialized
  initializeTawkWidget()
  
  // wait for tawk.to widget to be available
  const tawkAPI = await waitForTawkAPI()
  
  if (!tawkAPI) {
    console.warn(' could not access tawk.to API')
    return false
  }
  
  try {
    // Get selected language
    const language = getSelectedLanguage()
    
    // Common configuration for all views
    const baseAttributes = {
      language: language,
      location: viewName
    }
    
    if (viewName === 'landing') {
      // Attributes for TechnopawsView
      tawkAPI.setAttributes({
        ...baseAttributes,
        page: 'landing'
      }, (error) => {
        if (error) {
          console.error('Error configuring tawk.to for landing:', error)
        }
      })
    } 
    else if (viewName === 'login') {
      // Attributes for LoginView
      tawkAPI.setAttributes({
        ...baseAttributes,
        page: 'login/Register'
      }, (error) => {
        if (error) {
          console.error('Error configuring tawk.to for login:', error)
        }
      })
    } 
    else if (viewName === 'app') {
      // Attributes for HomeView (authenticated view)
      const user = auth.currentUser
      
      if (!user) {
        console.warn('No authenticated user to set attributes for app')
        return false
      }
      
      try {
        // Get additional user data from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid))
        
        if (userDoc.exists()) {
          const userData = userDoc.data()
          
          tawkAPI.setAttributes({
            email: user.email || '',
            name: userData.storeName || '',
            phone: userData.phoneNumber || '',
            location: 'app'
          }, (error) => {
            if (error) {
              console.error('Error configuring tawk.to for app:', error)
            }
          })
        } else {
          // if no user data, at least send the email
          tawkAPI.setAttributes({
            email: user.email || '',
            location: 'app'
          }, (error) => {
            if (error) {
              console.error('Error configuring tawk.to for app:', error)
            }
          })
        }
      } catch (error) {
        console.error('Error getting user data:', error)
        return false
      }
    } else {
      console.warn('Unknown view for tawk.to:', viewName)
      return false
    }
    
    return true
  } catch (error) {
    console.error('Error initializing tawk.to:', error)
    return false
  }
}

/**
 * Wait until the tawk.to API is available
 * @returns {Promise<object|null>} - The tawk.to API or null if not available
 */
function waitForTawkAPI() {
  return new Promise((resolve) => {
    const maxAttempts = 20
    let attempts = 0
    
    const checkTawk = () => {
      attempts++
      
      if (attempts > maxAttempts) {
        console.warn(`Tawk.to API not available after ${maxAttempts} attempts`)
        resolve(null)
        return
      }
      
      if (window.Tawk_API && typeof window.Tawk_API.setAttributes === 'function') {
        resolve(window.Tawk_API)
      } else {
        setTimeout(checkTawk, 300)
      }
    }
    
    checkTawk()
  })
}
